/**
 * Zustand 채팅 상태 관리 스토어
 * 채점봇과의 대화 메시지 및 상태 관리
 */

import { create } from 'zustand';
import { ChatMessage, BotEmotion } from '../types/chat';

/**
 * 채팅 스토어 상태 인터페이스
 */
interface ChatStoreState {
  // 메시지 목록
  messages: ChatMessage[];

  // 채점봇 타이핑 상태
  isBotTyping: boolean;

  // 현재 채점봇 감정
  currentBotEmotion: BotEmotion;

  // Actions
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => void;
  addBotMessage: (content: string, emotion?: BotEmotion, isSystem?: boolean, latex?: string) => void;
  addStudentMessage: (content: string) => void;
  setBotTyping: (isTyping: boolean) => void;
  setBotEmotion: (emotion: BotEmotion) => void;
  clearMessages: () => void;
}

/**
 * 채팅 스토어
 */
export const useChatStore = create<ChatStoreState>((set) => ({
  // 초기 상태
  messages: [],
  isBotTyping: false,
  currentBotEmotion: 'neutral',

  /**
   * 메시지 추가 (범용)
   * @param message - 메시지 정보 (id와 timestamp는 자동 생성)
   */
  addMessage: (message) => {
    const newMessage: ChatMessage = {
      ...message,
      id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
    };

    set((state) => ({
      messages: [...state.messages, newMessage],
    }));
  },

  /**
   * 채팅봇 메시지 추가 (타이핑 애니메이션 포함)
   * @param content - 메시지 내용
   * @param emotion - 봇 감정 상태
   * @param isSystem - 시스템 메시지 여부
   * @param latex - LaTeX 수식
   */
  addBotMessage: (content, emotion = 'neutral', isSystem = false, latex) => {
    set({ isBotTyping: true, currentBotEmotion: emotion });

    // 타이핑 애니메이션 딜레이 (500ms)
    setTimeout(() => {
      set((state) => ({
        messages: [
          ...state.messages,
          {
            id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            sender: 'bot',
            content,
            timestamp: new Date(),
            emotion,
            isSystem,
            latex,
          },
        ],
        isBotTyping: false,
      }));
    }, 500);
  },

  /**
   * 학생 메시지 추가
   * @param content - 메시지 내용
   */
  addStudentMessage: (content) => {
    set((state) => ({
      messages: [
        ...state.messages,
        {
          id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          sender: 'student',
          content,
          timestamp: new Date(),
        },
      ],
    }));
  },

  /**
   * 봇 타이핑 상태 설정
   */
  setBotTyping: (isTyping) => {
    set({ isBotTyping: isTyping });
  },

  /**
   * 봇 감정 상태 설정
   */
  setBotEmotion: (emotion) => {
    set({ currentBotEmotion: emotion });
  },

  /**
   * 메시지 전체 삭제
   */
  clearMessages: () => {
    set({ messages: [], isBotTyping: false, currentBotEmotion: 'neutral' });
  },
}));
